import { cn } from "@/lib/utils";

// ── PhaseTrack (Jeff 10/26 redesign asks) ───────────────────────────────────
// The three things the pages carried as prose and Jeff wants to SEE:
//   StageTrack — the five-step work ladder (INTAKE ▸ PLAN ▸ EXECUTE ▸ TEST ▸
//                CLOSE) with the chunk's current step filled and done steps
//                solid — "phases they're going through", one glance.
//   PhaseBar   — the chunk's planned steps as tick marks with the live phase
//                pulsing, plus the real "checks verified" counter — progress
//                you can feel, not a paragraph.
//   ModelChip  — what is actually running it: LOCAL (a Mac lane), OPENROUTER
//                (routed), or CLOUD (direct cloud model) — Jeff's "what model
//                is being used".
// Data rules (inherited from the band): every mark derives from the card's own
// payload — state, detail.phases/phaseCount, next ("Next: Phase 3 …" tells us
// the live phase), checksVerified/checksTotal. Nothing is inferred that the
// card does not say; a chunk without phases draws the ladder only.

export const STAGE_ORDER = ["INTAKE", "PLAN", "EXECUTE", "TEST", "CLOSE"];
const STAGE_WORD = {
  INTAKE: "Intake",
  PLAN: "Plan",
  EXECUTE: "Execute",
  TEST: "Test",
  CLOSE: "Close",
};

// "Next: Phase 7 (teaching capture) — …" / "Next: CLOSE — …" → the live step.
export const parseNextPhase = (next) => {
  const t = String(next || "");
  const close = /next:\s*close/i.test(t);
  if (close) return { kind: "close" };
  const m = /next:\s*phase\s*(\d+)/i.exec(t) || /phase\s*(\d+)/i.exec(t);
  return m ? { kind: "phase", n: Number(m[1]) } : null;
};

// The stage ladder: done steps solid ink, the current step the state's tone,
// future steps hollow. Mono initials under the dots (I · P · E · T · C).
export const StageTrack = ({ state, className }) => {
  const cur = STAGE_ORDER.indexOf(String(state || "EXECUTE"));
  const idx = cur < 0 ? 2 : cur;
  return (
    <div
      data-testid="stage-track"
      data-state={state}
      aria-label={`Stage: ${STAGE_WORD[STAGE_ORDER[idx]]}`}
      className={cn("flex w-full items-center gap-0", className)}
    >
      {STAGE_ORDER.map((s, i) => {
        const done = i < idx;
        const isCur = i === idx;
        return (
          <div key={s} className="flex min-w-0 flex-1 items-center">
            <span
              aria-hidden="true"
              data-testid={`stage-dot-${s}`}
              className={cn(
                "h-2 w-2 shrink-0 rounded-full transition-colors",
                done && "bg-os-ink",
                isCur && "bg-os-ink ring-2 ring-os-ink/20",
                !done && !isCur && "bg-os-border",
                isCur && s === "EXECUTE" && "animate-pulse",
              )}
            />
            {i < STAGE_ORDER.length - 1 ? (
              <span
                aria-hidden="true"
                className={cn(
                  "mx-0.5 h-px flex-1",
                  i < idx ? "bg-os-ink/50" : "bg-os-border",
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

// The planned steps as ticks: filled = passed, pulsing amber = the phase
// Kit's own "next" says it is on, hollow = ahead. Close-out reads full.
export const PhaseBar = ({ payload }) => {
  const detail = payload?.detail || {};
  const count = Number(payload?.phaseCount) || (detail.phases || []).length;
  const checks = detail.successTest || {};
  const next = parseNextPhase(payload?.next);
  if (!count) return null;
  const shown = Math.min(count, 12);
  const curN = next ? next.n : null;
  const closeAt = next && next.kind === "close" ? shown + 1 : null;
  return (
    <div
      data-testid="phase-bar"
      className="flex flex-wrap items-center gap-x-2 gap-y-1"
    >
      <span
        aria-hidden="true"
        className="flex shrink-0 items-center gap-[3px]"
      >
        {Array.from({ length: shown }, (_, i) => {
          const passed = closeAt !== null ? i < closeAt - 1 : curN !== null && i < curN - 1;
          const isCur = curN !== null && i === curN - 1 && closeAt === null;
          return (
            <span
              key={i}
              data-testid={`phase-tick-${i + 1}`}
              className={cn(
                "h-1.5 w-3 rounded-segment transition-colors",
                passed && "bg-os-ink",
                isCur && "animate-pulse bg-os-amber",
                !passed && !isCur && "bg-os-border",
              )}
            />
          );
        })}
      </span>
      <span
        data-testid="phase-bar-label"
        className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3"
      >
        {next && next.kind === "phase"
          ? `phase ${next.n}/${count}`
          : next && next.kind === "close"
            ? `phase ${count}/${count} · closing`
            : `${count} steps`}
      </span>
      {(Number(checks.total) || 0) > 0 ? (
        <span
          data-testid="phase-checks"
          className={cn(
            "font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
            (Number(checks.verified) || 0) >= Number(checks.total)
              ? "text-os-green"
              : "text-os-ink-3",
          )}
        >
          {checks.verified ?? checks.checked ?? 0}/{checks.total} checks
        </span>
      ) : null}
    </div>
  );
};

// What is running it. Local names arrive from the machine lanes (the Mac's
// Ollama lanes — known at render time on the pages that have them); the lane
// board's model strings classify the rest.
const CLOUD_HINT =
  /(minimax|kimi|glm|gpt|claude|gemini|deepseek|qwen|nova|mistral-large|grok)/i;

export const modelOrigin = (model, localNames = []) => {
  const m = String(model || "").trim();
  if (!m) return null;
  if (localNames.some((n) => m.includes(n) || n.includes(m))) {
    return { word: "Local · Mac", tone: "green" };
  }
  if (/openrouter/i.test(m)) return { word: "OpenRouter", tone: "amber" };
  if (CLOUD_HINT.test(m)) return { word: "Cloud", tone: "text-os-ink-3" };
  return { word: "Model", tone: "text-os-ink-3" };
};

const MODEL_TONE = {
  green: "text-os-green border-os-border-strong",
  amber: "text-os-amber border-os-border-strong",
  "text-os-ink-3": "text-os-ink-3 border-os-border",
};

export const ModelChip = ({ model, localNames }) => {
  const o = modelOrigin(model, localNames);
  if (!o) return null;
  return (
    <span
      data-testid="model-chip"
      className={cn(
        "shrink-0 whitespace-nowrap rounded-chip border px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
        MODEL_TONE[o.tone] || MODEL_TONE["text-os-ink-3"],
      )}
      title={`${o.word} — ${model}`}
    >
      {o.word}
    </span>
  );
};